import React, { ReactElement, FC } from 'react';
import { gqlBillingPlan, gqlCountry, gqlCurrency } from '@minecraft.graphql-types';
import { PlanTermEnum } from '@minecraft.graphql-operations';
import Trans from '../Trans';
import { getCurrencyCodeLabel } from './supportedCurrencyCode';

export type FormattedPriceMonthProps = {
  planTermCode?: gqlBillingPlan['billingCode'];
  planCurrencyCode?: gqlCurrency['code'];
  countryCode: gqlCountry['code'];
  isSingleItemPrice?: boolean;
  price: number;
  PriceComponent: ReactElement;
  LabelComponent: ReactElement;
  displayPlanCurrencyCode?: boolean;
};

const getI18nKey = (
  planTermCode?: gqlBillingPlan['billingCode'],
  price?: number,
  isSingleItemPrice?: boolean
): string => {
  if (!planTermCode) {
    return isSingleItemPrice
      ? 'common:paywall.currency.formattedPlanPriceMonthMediaPurchase'
      : 'common:paywall.currency.formattedPlanPriceMonthWithComponents';
  }
  return price === 0
    ? 'common:paywall.currency.formattedPlanZeroPriceMonthWithComponents'
    : 'common:paywall.currency.formattedPlanPriceMonthWithComponents';
};

export const FormattedPriceMonth: FC<FormattedPriceMonthProps> = React.memo(
  ({
    planTermCode,
    planCurrencyCode = 'USD',
    countryCode,
    price,
    PriceComponent,
    LabelComponent,
    isSingleItemPrice,
    displayPlanCurrencyCode = false,
  }) => {
    const i18nKey = getI18nKey(planTermCode, price, isSingleItemPrice);
    const adjustedPrice = planTermCode === PlanTermEnum.Annual ? price / 12 : price;

    return (
      <Trans
        i18nKey={i18nKey}
        values={{
          price: adjustedPrice,
          currencyCode: planCurrencyCode,
          displayedCurrencyCode: getCurrencyCodeLabel(planCurrencyCode, countryCode, displayPlanCurrencyCode),
        }}
        components={{
          price: PriceComponent,
          label: LabelComponent,
        }}
      />
    );
  }
);
